import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { gsap, MOTION_QUERIES } from '@/lib/motion'

type CursorMode = 'default' | 'link' | 'label'

// A follower that sits under the native pointer: a small difference-blended dot
// that swells over interactive elements, and a lime label for anything marked
// with data-cursor="Label". Only mounted for fine pointers without reduced motion.
const CursorFollower: FC = (): ReactNode => {
    const dotRef = useRef<HTMLDivElement>(null)
    const bubbleRef = useRef<HTMLDivElement>(null)
    const [mode, setMode] = useState<CursorMode>('default')
    const [label, setLabel] = useState('')

    useEffect(() => {
        const dot = dotRef.current
        const bubble = bubbleRef.current

        if (!dot || !bubble) return

        gsap.set([dot, bubble], { xPercent: -50, yPercent: -50, x: -100, y: -100 })

        const dotX = gsap.quickTo(dot, 'x', { duration: 0.16, ease: 'power3.out' })
        const dotY = gsap.quickTo(dot, 'y', { duration: 0.16, ease: 'power3.out' })
        const bubbleX = gsap.quickTo(bubble, 'x', { duration: 0.55, ease: 'power3.out' })
        const bubbleY = gsap.quickTo(bubble, 'y', { duration: 0.55, ease: 'power3.out' })
        let visible = false

        const onMove = (event: PointerEvent): void => {
            if (event.pointerType !== 'mouse') return

            if (!visible) {
                visible = true
                gsap.set([dot, bubble], { x: event.clientX, y: event.clientY })
                gsap.to(dot, { opacity: 1, duration: 0.3 })
            }

            dotX(event.clientX)
            dotY(event.clientY)
            bubbleX(event.clientX)
            bubbleY(event.clientY)
        }

        const onOver = (event: PointerEvent): void => {
            const target = event.target as Element | null

            if (!target || typeof target.closest !== 'function') return

            const labelled = target.closest<HTMLElement>('[data-cursor]')

            if (labelled) {
                setLabel(labelled.dataset.cursor ?? '')
                setMode('label')
                return
            }

            const interactive = target.closest('a, button, [role="button"], label, select, summary, input[type="checkbox"], input[type="radio"]')

            setMode(interactive ? 'link' : 'default')
        }

        const onLeave = (): void => {
            visible = false
            gsap.to(dot, { opacity: 0, duration: 0.2 })
            setMode('default')
        }

        window.addEventListener('pointermove', onMove, { passive: true })
        document.addEventListener('pointerover', onOver, { passive: true })
        document.documentElement.addEventListener('pointerleave', onLeave)

        return () => {
            window.removeEventListener('pointermove', onMove)
            document.removeEventListener('pointerover', onOver)
            document.documentElement.removeEventListener('pointerleave', onLeave)
        }
    }, [])

    return (
        <div aria-hidden='true' className='pointer-events-none fixed inset-0 z-[90]'>
            <div
                ref={dotRef}
                className={cn(
                    'fixed left-0 top-0 rounded-full bg-white opacity-0 mix-blend-difference transition-[width,height] duration-500 ease-expo',
                    mode === 'default' && 'size-2.5',
                    mode === 'link' && 'size-14',
                    mode === 'label' && 'size-0'
                )}
            />

            <div ref={bubbleRef} className='fixed left-0 top-0'>
                <div
                    className={cn(
                        'type-label flex size-24 items-center justify-center rounded-full bg-volt px-3 text-center text-ink transition-transform duration-500 ease-expo',
                        mode === 'label' ? 'scale-100' : 'scale-0'
                    )}
                >
                    {label}
                </div>
            </div>
        </div>
    )
}

const Cursor: FC = (): ReactNode => {
    const [enabled, setEnabled] = useState(false)

    useEffect(() => {
        const query = window.matchMedia(MOTION_QUERIES.fine)
        const update = (): void => setEnabled(query.matches)

        update()
        query.addEventListener('change', update)

        return () => query.removeEventListener('change', update)
    }, [])

    return enabled ? <CursorFollower /> : null
}

export default Cursor
